import { EditablePriceScale } from '../../api/asset/assetApiTypes';

/**
 * Adding and removing class-specific tariff rows (#1808).
 *
 * Extracted from `PricingSection` for the same reason `priceScalePayload` was:
 * the bug is invisible by inspection. A class rate is an **additional row** of
 * the same shape as a general one, carrying a class id — not a property of the
 * general row. So editing one means inserting, updating or deleting an entry
 * in the scales array, and getting that wrong either loses a rate silently or
 * creates a second row the backend rejects as a duplicate scope.
 *
 * Two rules worth stating because neither is guessable:
 *
 * - **A blank field removes the row**, it does not store 0. An absent class row
 *   falls back to the general rate; a `0` row makes that segment free. Those
 *   are opposite outcomes and the UI offers no other way to distinguish them.
 * - **Shape means the whole scope** — days, duration, full-day flag and time
 *   window. The backend matches rows on exactly that plus the class
 *   (`scopeKey` / `makeKey`), and archives whatever it cannot match, so a
 *   looser comparison here would make a price edit delete rates.
 */

/** Do two rows describe the same tariff scope, class aside? */
export const sameScaleShape = (
    a: EditablePriceScale,
    b: EditablePriceScale,
): boolean =>
    (a.days ?? null) === (b.days ?? null)
    && a.durationInMinutes === b.durationInMinutes
    && (a.isFullDay ?? null) === (b.isFullDay ?? null)
    && (a.startTime ?? null) === (b.startTime ?? null)
    && (a.endTime ?? null) === (b.endTime ?? null);

/** The rows that carry no class — the general rates. */
export const generalRows = (scales: EditablePriceScale[]): EditablePriceScale[] =>
    scales.filter((s) => !s.customerClassId);

/** The class row matching a general row's shape, if the tenant set one. */
export const classRowFor = (
    scales: EditablePriceScale[],
    classId: string,
    base: EditablePriceScale,
): EditablePriceScale | undefined =>
    scales.find((s) => s.customerClassId === classId && sameScaleShape(s, base));

/**
 * The scales array after the operator typed `raw` into one class rate field.
 *
 * `raw === ''` removes the row. An unparseable value returns the array
 * unchanged — the same "ignore it" behaviour the general price field has, so
 * half-typed input never destroys a rate.
 */
export const applyClassPrice = (
    scales: EditablePriceScale[],
    classId: string,
    base: EditablePriceScale,
    raw: string,
): EditablePriceScale[] => {
    const existing = classRowFor(scales, classId, base);

    if (raw === '') {
        return existing ? scales.filter((s) => s !== existing) : scales;
    }

    const parsed = parseFloat(raw);
    if (Number.isNaN(parsed)) return scales;

    if (existing) {
        return scales.map((s) => (s === existing ? { ...s, price: parsed } : s));
    }

    // `id` is deliberately absent: this row does not exist server-side yet,
    // and the save path creates any incoming row it cannot match.
    return [
        ...scales,
        {
            price: parsed,
            days: base.days ?? null,
            durationInMinutes: base.durationInMinutes,
            isFullDay: base.isFullDay,
            startTime: base.startTime ?? null,
            endTime: base.endTime ?? null,
            additionalDaysPrice: base.additionalDaysPrice ?? null,
            customerClassId: classId,
        },
    ];
};
